import React, { useMemo } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  useTheme,
  Divider,
} from "@mui/material";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { useGetInvoiceQuery } from "api/api-invoices";
import { useGetCurrentUserQuery } from "api/api-users-v2";
import TooltipMui from "@mui/material/Tooltip";

// Colors for the chart
const COLORS = [
  "#0088FE", "#00C49F", "#FFBB28", "#FF8042",
  "#8884d8", "#82ca9d", "#ffc658", "#ff7300",
];
const OTHERS_COLOR = "#999999";

interface ImporterExposureProps {
  data?: any;
  tabIndex: string; // Accept tabIndex as a prop
}

const ImporterExposure: React.FC<ImporterExposureProps> = ({ data, tabIndex }) => {
  const theme = useTheme();

  // Fetch user and invoice data
  const { data: currentUser } = useGetCurrentUserQuery();
  const id = currentUser?.id;

  const { data: fetchedData, isLoading } = useGetInvoiceQuery(
    { endpoint: `dashboard/investors-summary` },
    {
      skip: !!data || !id,
      refetchOnMountOrArgChange: true,
    }
  );

  const invoiceData = data || fetchedData;

  // Transform invoice data based on selected tab
  const chartData = useMemo(() => {
    const invoices = invoiceData?.invoices || [];
    
    // Filter out pre-listing invoices to match allocations table
    const activeInvoices = invoices.filter((inv: any) => {
      return inv.phase !== "0_PRE_LISTING" && 
             inv.phase !== "PRE_LISTING" && 
             inv.status !== "pre_listing" &&
             inv.status !== "prelisting";
    });
    
    const total = activeInvoices.reduce(
      (sum: any, inv: any) => sum + (Number(inv.principle_amount) || 0),
      0
    );
    if (!total) return [];

    const map = new Map<string, number>();
    activeInvoices.forEach((inv: any) => {
      let key: string;
      switch (tabIndex) {
        case "importer":
          key = inv.buyer_name || "Unknown";
          break;
        case "exporter":
          key = inv.supplier_name || "Unknown";
          break;
        case "importer_countries":
          key = inv.importer_country || "Unknown";
          break;
        case "exporter_countries":
          key = inv.exporter_country || "Unknown";
          break;
        case "industry":
          key = inv.sector || "Unknown";
          break;
        default:
          key = "Unknown";
      }
      map.set(key, (map.get(key) || 0) + (inv.principle_amount || 0));
    });

    const entries = Array.from(map.entries())
      .map(([name, value]) => ({
        name,
        value,
        percentage: (value / total) * 100,
      }))
      .sort((a, b) => b.value - a.value);

    // Handle top N entries and group "Others" if needed
    const maxEntries = tabIndex === "industry" ? 6 : 10;
    const top = entries.slice(0, maxEntries);
    const others = entries.slice(maxEntries);

    if (others.length && tabIndex === "industry") {
      const othersValue = others.reduce((sum, item) => sum + item.value, 0);
      if (othersValue > 0) {
        top.push({
          name: "Others",
          value: othersValue,
          percentage: (othersValue / total) * 100,
        });
      }
    }

    return top.map((entry) => ({
      ...entry,
      displayName: `${entry.name} (${entry.percentage.toFixed(2)}%)`,
    }));
  }, [tabIndex, invoiceData]);

  // Custom legend formatter to always show displayName (name + percentage)
  const renderLegendText = (value: string) => {
    const entry = chartData.find((item) => item.displayName === value || item.name === value);
    // Always show displayName (name + percentage)
    if (entry) {
      // If the displayName is too long, show a tooltip
      const maxLength = 22;
      const showTooltip = entry.displayName.length > maxLength;
      const legendContent = (
        <span style={{
          display: 'inline-block',
          maxWidth: 160,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          verticalAlign: 'middle',
        }}>
          {entry.displayName}
        </span>
      );
      return showTooltip ? (
        <TooltipMui title={entry.displayName} placement="left" arrow>
          {legendContent}
        </TooltipMui>
      ) : legendContent;
    }
    return value;
  };

  // Total value displayed in the center of the pie
  const totalAmount = useMemo(
    () => chartData.reduce((sum, item) => sum + item.value, 0),
    [chartData]
  );

  const formatCurrency = (val: number) =>
    val.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    });

  const renderCenterLabel = () => (
    <text
      x="50%"
      y="50%"
      textAnchor="middle"
      dominantBaseline="middle"
      style={{
        fontSize: "0.8rem",
        fontWeight: 500,
        fill: theme.palette.text.primary,
      }}
    >
      {formatCurrency(totalAmount)}
    </text>
  );

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload?.[0]) {
      // Get the name from the payload
      const payloadName = payload[0].payload.name || payload[0].name;
      const payloadValue = payload[0].value;
      
      // Find the entry with matching name or value
      const entry = chartData.find((item) => 
        item.name === payloadName || 
        item.value === payloadValue ||
        payload[0].payload === item
      );
      
      // Calculate percentage for display
      const totalValue = chartData.reduce((sum, item) => sum + item.value, 0);
      const percentage = totalValue > 0 ? ((payloadValue / totalValue) * 100).toFixed(2) : '0.00';
      
      return (
        <Box
          sx={{
            backgroundColor: "white",
            p: 1,
            border: "1px solid #ccc",
            borderRadius: "4px",
            boxShadow: 2,
          }}
        >
          <Typography sx={{ fontSize: "0.75rem", fontWeight: 'bold' }}>
            {entry?.displayName || `${payloadName} (${percentage}%)`}
          </Typography>
          <Typography sx={{ fontSize: "0.75rem" }}>
            {formatCurrency(payloadValue)}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  return (
    <Card sx={{ borderRadius: 1, boxShadow: 1 }}>
      <CardContent sx={{ p: { xs: 1.5, md: 2 } }}>
        <Box display="flex" flexDirection="column">
          {/* Header */}
          <Typography
            variant="subtitle1"
            color="primary.main"
            sx={{ fontWeight: 500, fontSize: { xs: "0.875rem", md: "1rem" } }}
          >
            {tabIndex === "importer" && "Importers Exposure"}
            {tabIndex === "exporter" && "Exporters Exposure"}
            {tabIndex === "industry" && "Sectors Exposure"}
            {tabIndex === "importer_countries" && "Importer Countries"}
            {tabIndex === "exporter_countries" && "Exporter Countries"}
          </Typography>

          <Divider sx={{ mb: 1 }} />

          {/* Pie Chart */}
          <Box sx={{ display: "flex", justifyContent: "center", minHeight: 240 }}>
            {isLoading ? (
              <CircularProgress size={24} />
            ) : chartData.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No data available
              </Typography>
            ) : (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={85}
                    innerRadius={38}
                    fill="#8884d8"
                    dataKey="value"
                    nameKey="displayName"
                    label={renderCenterLabel}
                    paddingAngle={1}
                    cornerRadius={2}
                  >
                    {chartData.map((entry) => (
                      <Cell
                        key={`cell-${entry.name}`}
                        fill={entry.name === "Others"
                          ? OTHERS_COLOR
                          : COLORS[chartData.indexOf(entry) % COLORS.length]}
                        stroke={theme.palette.background.paper}
                        strokeWidth={1}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend
                    layout="vertical"
                    verticalAlign="middle"
                    align="right"
                    formatter={renderLegendText}
                    wrapperStyle={{
                      fontSize: "0.65rem",
                      lineHeight: "1.5em",
                      maxWidth: "35%",
                    }}
                    iconType="circle"
                    iconSize={6}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ImporterExposure;
